// The player's clock (PRD-0074 slice 03): the boxes on the picture are drawn at the time it reports.

const CLOCK_EVENTS = ['play', 'pause', 'seeked'] as const

/** What the clock reads: a `<video>` element, or a test's stand-in. */
export type ClockMedia = EventTarget & Pick<HTMLMediaElement, 'currentTime' | 'paused' | 'ended'>

/** Where the next frame comes from: the browser's animation frames, or a test's. */
export type FrameScheduler = Pick<Window, 'requestAnimationFrame' | 'cancelAnimationFrame'>

/**
 * Report *media*'s time to *onTime* once a frame while it plays, and once on every play, pause
 * and seek, with one frame pending at most. A seek that lands while it plays keeps the frames
 * coming: "Play pass" seeks and plays at once, its `seeked` arrives after `play` and no further
 * `play` follows, so a clock that stopped on `seeked` froze the boxes until a click on the
 * player. Returns the unsubscribe.
 */
export function followPlayback(
  media: ClockMedia,
  onTime: (seconds: number) => void,
  frames: FrameScheduler = window
): () => void {
  let frame = 0
  const tick = () => {
    onTime(media.currentTime)
    frames.cancelAnimationFrame(frame)
    if (!media.paused && !media.ended) frame = frames.requestAnimationFrame(tick)
  }
  for (const type of CLOCK_EVENTS) media.addEventListener(type, tick)
  return () => {
    frames.cancelAnimationFrame(frame)
    for (const type of CLOCK_EVENTS) media.removeEventListener(type, tick)
  }
}
